import { useState } from 'react';
import { useInput, InputHelperText, Labeled } from 'react-admin';
import { Box, Button, Stack, Typography, CircularProgress, IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Close';
import UploadIcon from '@mui/icons-material/CloudUpload';
import { uploadFile } from '../providers/dataProvider.js';

/**
 * Stores the URL of the uploaded image as a plain string in the form.
 * Uploads immediately on file selection so the rest of the form save
 * stays a simple JSON PUT/POST.
 *
 * Usage: <ImageUploadInput source="image" label="Cover image" />
 */
export function ImageUploadInput(props) {
  const { source, label, helperText, validate } = props;
  const { field, fieldState, isRequired } = useInput({ source, validate });
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  const url = field.value;

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const result = await uploadFile(file);
      field.onChange(result.url);
    } catch (err) {
      setError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
      // Reset input so selecting the same file again still fires onChange
      e.target.value = '';
    }
  };

  const clear = () => field.onChange('');

  return (
    <Labeled label={label || source} isRequired={isRequired} fullWidth>
      <Box sx={{ width: '100%' }}>
        {url ? (
          <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ mb: 1 }}>
            <Box
              component="img"
              src={url}
              alt=""
              sx={{
                width: 220,
                height: 140,
                objectFit: 'cover',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
                background: '#f5f5f5',
              }}
            />
            <Stack spacing={1}>
              <Typography variant="caption" sx={{ wordBreak: 'break-all', maxWidth: 400 }}>
                {url}
              </Typography>
              <Box>
                <IconButton size="small" onClick={clear} aria-label="Clear image">
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            </Stack>
          </Stack>
        ) : (
          <Box
            sx={{
              width: 220,
              height: 140,
              border: '1px dashed',
              borderColor: 'divider',
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'text.secondary',
              mb: 1,
            }}
          >
            <Typography variant="caption">No image</Typography>
          </Box>
        )}

        <Button
          component="label"
          variant="outlined"
          size="small"
          startIcon={uploading ? <CircularProgress size={14} /> : <UploadIcon />}
          disabled={uploading}
        >
          {uploading ? 'Uploading…' : (url ? 'Replace' : 'Upload image')}
          <input type="file" hidden accept="image/*" onChange={handleFile} />
        </Button>

        <InputHelperText
          touched={fieldState.isTouched}
          error={error || fieldState.error?.message}
          helperText={helperText}
        />
      </Box>
    </Labeled>
  );
}
